import { Card, FormRow, InputDatePicker, Select } from "@components/ui-kit";
import { t } from "@lingui/macro";
import { DocumentSequenceAutocompleteField } from "@src/components/ui-kit/AutocompleteField/variants/invoice-sequence";
import { cannot } from "@src/utils/components/permissions";
import { useStore as useAppStore } from "@src/utils/hooks";
import { FormMode } from "@src/utils/types";
import { BankAccountSelect } from "@src/widgets/BankAccountSelect";
import { CompanySelect } from "@src/widgets/CompanySelect";
import { observer } from "mobx-react-lite";
import { useScrollToWhen } from "../../hooks";
import { useStore } from "../../store";
import { RelatedDocuments } from "../related-documents";

export const GeneralInfo = observer(function GeneralInfo() {
  const { workspaceStore } = useAppStore();
  const store = useStore();
  const { form } = store;

  const onSelectCurrency = (value: string) => {
    form.currency_id.onChange(value);
    store.updateNumberFieldsDenominator();
  };

  const divRef = useScrollToWhen(store.scrollTo.expenseDataCard_generalInfo);

  return (
    <Card.Section>
      <div ref={divRef}>
        <FormRow title={t`Created date`}>
          <InputDatePicker
            isDisabled={cannot("expense_accessAccountingAttributes")}
            selected={form.userCreatedDate.value}
            onChange={store.handleChangeCreatedDate}
            error={form.userCreatedDate.error}
          />
        </FormRow>
        {store.canUserAccess("issue_date") && (
          <FormRow title={t`Issue date`}>
            <InputDatePicker
              error={form.issue_date.error}
              selected={form.issue_date.$}
              onChange={(val) => {
                form.issue_date.onChange(val?.start);
                if (
                  (form.date_of_supply.dirty && form.date_of_supply.value) ||
                  store.mode === FormMode.EDIT
                ) {
                  return;
                }

                store.handleChangeDateOfSupply(val?.start, "reset");
              }}
            />
          </FormRow>
        )}
        {store.canUserAccess("date_of_supply") && (
          <FormRow title={t`Date of supply`}>
            <InputDatePicker
              error={form.date_of_supply.error}
              selected={form.date_of_supply.$}
              onChange={(val) => {
                store.handleChangeDateOfSupply(val?.start);
              }}
            />
          </FormRow>
        )}
        {store.canUserAccess("due_date") && (
          <FormRow title={t`Due date`}>
            <InputDatePicker
              error={form.due_date.error}
              selected={form.due_date.$}
              onChange={(val) => {
                form.due_date.onChange(val?.start);
              }}
            />
          </FormRow>
        )}
        {store.canUserAccess("workspace_company_id") && (
          <FormRow
            title={t`Legal entity`}
            display={
              workspaceStore.hasMultipleCompanies(
                form.workspace_company_id.value,
              ) || form.workspace_company_id.hasError
                ? "flex"
                : "none"
            }
          >
            <CompanySelect
              field={form.workspace_company_id}
              onChange={store.handleChangeLegalEntity}
            />
          </FormRow>
        )}
        {store.canUserAccess("workspace_bank_account_id") && (
          <FormRow title={t`Agency bank account`}>
            <BankAccountSelect
              field={form.workspace_bank_account_id}
              companyId={form.workspace_company_id.$}
            />
          </FormRow>
        )}
        {store.canUserAccess("expense_no") && (
          <FormRow title={t`Expense no`}>
            <DocumentSequenceAutocompleteField
              isLoading={store.isInvoiceNoFetching}
              autoCompleteOptions={store.expenseNoOptions}
              value={store.form.expense_no.value ?? ""}
              onChange={store.handleChangeInvoiceNo}
              onSelect={store.handleSelectInvoiceNo}
              onPopoverOpen={store.handleInvoiceAutocompletePopoverOpen}
              placeholder={t`Expense no`}
              error={form.expense_no.error || form.expense_sequence_id.error}
              noOptionsMessage={store.noSequenceOptionsMessage}
            />
          </FormRow>
        )}
        <FormRow title={t`Currency`}>
          <Select
            value={form.currency_id.value}
            options={store.currenciesOptions}
            onChange={onSelectCurrency}
          />
        </FormRow>
      </div>

      <RelatedDocuments store={store} />
    </Card.Section>
  );
});
